import { PickingInfo } from '@deck.gl/core';
import { IconLayer, ScatterplotLayer, TextLayer } from '@deck.gl/layers';
import { Route } from '@helpers/types';
import * as turf from '@turf/turf';
import { hexToRgb } from '@utils/colors';
import { Feature, Point, Position } from 'geojson';
import { chain } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { MapRef } from 'react-map-gl/maplibre';

export const useDeckPoints = (
  mapRef: React.RefObject<MapRef | null>,
  routes: Array<Route> | undefined,
) => {
  const [hoverInfo, setHoverInfo] = useState<PickingInfo | null>(null);
  const [selectedRoutes, setSelectedRoutes] = useState<string[]>([]);
  const [hoveredRoute, setHoveredRoute] = useState<string | null>(null);

  const focusRoute = useCallback(
    (selected: Array<string>) => {
      setSelectedRoutes(selected);
      if (!mapRef.current || !routes) return;

      const features = chain(routes)
        .filter((route) => selected.includes(route.name))
        .map((route) =>
          route.orders.map((order) =>
            turf.point(order.geometry.coordinates as Position),
          ),
        )
        .flatten()
        .value();

      if (features.length > 0) {
        const bbox = turf.bbox(turf.featureCollection(features));
        mapRef.current.fitBounds(
          [
            [bbox[0], bbox[1]],
            [bbox[2], bbox[3]],
          ],
          { padding: 40, duration: 1000 },
        );
      }
    },
    [routes, mapRef],
  );

  const layers = useMemo(() => {
    if (!routes) return [];

    const allPoints: Feature<Point>[] = chain(routes)
      .map((route) =>
        route.orders.map((order) => ({
          type: 'Feature' as const,
          geometry: order.geometry as Point,
          properties: {
            route: route.name,
            color: route.color,
            stop: order.delivery_order,
            id: order.tracking_id,
            internal_key: order.internal_key,
          },
        })),
      )
      .flatten()
      .value();

    // Base layer: Grey dots
    const baseLayer = new ScatterplotLayer({
      id: 'points-base',
      data: allPoints,
      pickable: true,
      stroked: true,
      filled: true,
      radiusMinPixels: 3,
      getPosition: (d: any) => d.geometry.coordinates,
      getFillColor: [174, 174, 174], // #AEAEAE
      getLineColor: [0, 0, 0],
      getLineWidth: 0.5,
      onHover: (info) => {
        setHoverInfo(info);
        if (info.object) {
          setHoveredRoute(info.object.properties.route);
        } else {
          setHoveredRoute(null);
        }
      },
    });

    // Highlight layer: Colored dots for selected or hovered routes
    const highlightPoints = allPoints.filter(
      (p) =>
        selectedRoutes.includes(p.properties!.route) ||
        p.properties!.route === hoveredRoute,
    );

    const highlightLayer = new ScatterplotLayer({
      id: 'points-highlight',
      data: highlightPoints,
      pickable: false, // let base layer handle picking to avoid z-fighting issues or complexity
      stroked: true,
      filled: true,
      radiusMinPixels: 4, // Slightly larger
      getPosition: (d: any) => d.geometry.coordinates,
      getFillColor: (d: any) => hexToRgb(d.properties.color),
      getLineColor: [0, 0, 0],
      getLineWidth: 0.5,
    });

    const PIN_ICON_URL = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
      `<svg width="44" height="50" viewBox="0 0 44 50" xmlns="http://www.w3.org/2000/svg"><path d="M 12 2 H 32 A 10 10 0 0 1 42 12 V 26 A 10 10 0 0 1 32 36 H 26 L 22 46 L 18 36 H 12 A 10 10 0 0 1 2 26 V 12 A 10 10 0 0 1 12 2 Z" fill="white" stroke="black" stroke-width="2"/></svg>`,
    )}`;

    // Icon background layer for labels
    const iconLayer = new IconLayer({
      id: 'points-labels-bg',
      data: highlightPoints,
      pickable: false,
      getPosition: (d: any) => d.geometry.coordinates,
      getIcon: () => ({
        url: PIN_ICON_URL,
        width: 44,
        height: 50,
        mask: false,
      }),
      getSize: 50,
      getPixelOffset: [0, -25], // Shift up by half height so tip is at point
      billboard: true,
    });

    // Text layer: Show stop numbers on highlighted points (outside the circle)
    const textLayer = new TextLayer({
      id: 'points-labels',
      data: highlightPoints,
      pickable: false,
      getPosition: (d: any) => d.geometry.coordinates,
      getText: (d: any) => String(d.properties.stop),
      getSize: 21,
      getColor: [50, 50, 50, 255], // Dark grey text (fully opaque)
      getTextAnchor: 'middle',
      getAlignmentBaseline: 'center',
      getPixelOffset: [0, -31], // -25 (center of icon) - 6 (shift to center of bubble)
      fontFamily: 'Arial, sans-serif',
      fontWeight: 'bold',
      billboard: true,
    });

    return [baseLayer, highlightLayer, iconLayer, textLayer];
  }, [routes, selectedRoutes, hoveredRoute]);

  return { layers, hoverInfo, focusRoute, hoveredRoute, selectedRoutes };
};